// =====================================================
// Swiss Finance OS - Protected Route Component
// Handles authentication-based route protection
// =====================================================

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: 'admin' | 'partner' | 'senior' | 'junior' | 'assistant';
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requireRole,
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const { user, profile, loading, isProfileLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner ONLY during initial auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-brand-primary" />
          <p className="text-muted-foreground font-inter">Authentifizierung prüfen...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Redirect new users (without profile) to onboarding
  // Skip this check if already on onboarding or demo pages
  const isOnboardingPage = location.pathname.includes('onboarding') || 
                          location.pathname.includes('demo');
  
  // Check if onboarding was completed (stored in localStorage after successful onboarding)
  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true';
  
  // CRITICAL FIX: Don't redirect while profile is still loading
  // Only redirect to onboarding if profile finished loading AND is null AND onboarding not completed
  if (!isProfileLoading && !profile && !isOnboardingPage && !onboardingCompleted) {
    console.log('New user without profile detected - redirecting to onboarding');
    return (
      <Navigate
        to="/structured-onboarding"
        replace
      />
    );
  }

  // Check role requirements if specified
  if (requireRole && profile) {
    const roleHierarchy = {
      'assistant': 0,
      'junior': 1,
      'senior': 2,
      'partner': 3,
      'admin': 4,
    };

    const userRoleLevel = roleHierarchy[profile.role];
    const requiredRoleLevel = roleHierarchy[requireRole];

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-6xl text-gray-300 mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Zugriff verweigert
            </h1>
            <p className="text-gray-600 mb-4">
              Sie haben nicht die erforderlichen Berechtigungen für diese Seite.
            </p>
            <p className="text-sm text-gray-500">
              Erforderliche Rolle: {requireRole} | Ihre Rolle: {profile.role}
            </p>
            <button
              onClick={() => window.history.back()}
              className="mt-4 px-4 py-2 bg-brand-primary text-brand-primary-foreground rounded-lg hover:bg-brand-primary/90 font-inter font-medium shadow-elegant transition-all"
            >
              Zurück
            </button>
          </div>
        </div>
      );
    }
  }

  // Render protected content
  return <>{children}</>;
}

// Higher-order component version for class components